import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { toast } from "react-toastify";

import ReportService from "src/Services/API/report.service";
import { can, handleError } from "src/Common/function";
import { signoffWeeksFor } from "./hours";

const describeWeek = (weekStart) =>
  `${moment(weekStart).format("MMM D")} – ${moment(weekStart).add(6, "days").format("MMM D")}`;
const when = (value) => (value ? moment(value).format("ddd MMM D, h:mm A") : "");

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CrossIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const HandIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const STATUS_STYLE = {
  SUBMITTED: { chip: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]", label: "Submitted", Icon: CheckIcon },
  REOPEN_REQUESTED: { chip: "bg-[#EDE9FE] text-[#5B21B6] border-[#DDD6FE]", label: "Reopen requested", Icon: HandIcon },
  REOPENED: { chip: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", label: "Reopened", Icon: HandIcon },
  OPEN: { chip: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]", label: "Not submitted", Icon: CrossIcon },
};

/**
 * The state of a technician's weekly sign-off for the weeks on screen, to the
 * right of History on their row: a green check when every finished week in
 * the range is submitted, a red cross when one is not, and the request or
 * reopen state when one is in play. Clicking opens what the office can do
 * about it — answer a request, reopen, remind, or sign on their behalf — each
 * behind its own permission.
 *
 * The weeks are the technician's own cycle weeks, whatever the selector says,
 * so a Monday-to-Sunday person is judged on the week they actually signed.
 */
const SignOffChip = ({ technician, signoff, range, history, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [busy, setBusy] = useState(null); // `${weekStart}|${action}`
  // One reason box at a time: which week and which action it is for.
  const [draft, setDraft] = useState(null); // { weekStart, action, reason }
  const [channels, setChannels] = useState({ push: true, email: true, sms: false });
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  const weeks = useMemo(() => signoffWeeksFor(signoff, range), [signoff, range]);

  const canDecide = can("time-management:decideReopen");
  const canRemind = can("time-management:sendReminder");
  const canSubmitFor = can("time-management:submitForTechnician");

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const WIDTH = 380;
      setRect({
        top: Math.round(anchor.bottom + 6),
        left: Math.round(Math.max(8, Math.min(anchor.left, window.innerWidth - WIDTH - 8))),
        width: WIDTH,
      });
    };
    place();
    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!weeks.length || technician.technicianId == null) return null;

  // The chip says the one thing that needs doing, if anything does.
  const counts = weeks.reduce((acc, week) => ({ ...acc, [week.status]: (acc[week.status] ?? 0) + 1 }), {});
  const headline = counts.REOPEN_REQUESTED
    ? "REOPEN_REQUESTED"
    : counts.OPEN
      ? "OPEN"
      : counts.REOPENED
        ? "REOPENED"
        : "SUBMITTED";
  const style = STATUS_STYLE[headline];
  const chipText =
    weeks.length === 1
      ? style.label
      : headline === "SUBMITTED"
        ? `All ${weeks.length} weeks submitted`
        : headline === "OPEN"
          ? `${counts.OPEN} of ${weeks.length} weeks not submitted`
          : `${style.label} · ${counts[headline]} of ${weeks.length}`;
  const title = weeks
    .map((week) => `${describeWeek(week.weekStart)}: ${STATUS_STYLE[week.status].label}`)
    .join("\n");

  // Who did it, from the ledger the page already holds: the submission row
  // carries ids, the history carries names.
  const actorFor = (kind, weekStart) => {
    const match = (history || [])
      .filter((event) => event.kind === kind && event.date === weekStart)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    return match?.actorName ?? null;
  };

  const run = async (weekStart, action, call, success) => {
    setBusy(`${weekStart}|${action}`);
    try {
      const response = await call();
      toast.success(typeof success === "function" ? success(response?.data) : success);
      setDraft(null);
      onChanged?.();
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(null);
    }
  };

  const name = technician.technician;
  const reasonBox = (week, action, placeholder, submitLabel, onSubmit, required = true) =>
    draft?.weekStart === week.weekStart && draft?.action === action ? (
      <div className="mt-2">
        <textarea
          autoFocus
          value={draft.reason}
          onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
          placeholder={placeholder}
          rows={2}
          className="w-full rounded-[6px] border border-[#C9CEE2] px-2.5 py-1.5 font-InterRegular text-[12px] leading-4 text-[#1A2332] resize-none focus:outline-none focus:border-[#0065FD]"
        />
        <div className="mt-1.5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="px-2.5 py-1 rounded-[6px] text-[12px] font-InterMedium text-[#5F6477] hover:bg-[#F0F1F5]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={(required && !draft.reason.trim()) || busy != null}
            onClick={() => onSubmit(draft.reason.trim())}
            className="px-2.5 py-1 rounded-[6px] text-[12px] font-InterMedium text-white bg-[#0065FD] disabled:bg-[#CAD5E2]"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    ) : null;

  const actionButton = (label, onClick, tone = "blue") => (
    <button
      type="button"
      disabled={busy != null}
      onClick={onClick}
      className={`px-2.5 py-1 rounded-[6px] border text-[12px] leading-4 font-InterMedium whitespace-nowrap disabled:opacity-60 ${
        tone === "red"
          ? "border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C] hover:bg-[#FEE2E2]"
          : tone === "green"
            ? "border-[#BBF7D0] bg-[#F0FDF4] text-[#166534] hover:bg-[#DCFCE7]"
            : "border-[#C7DBFF] bg-[#EEF4FF] text-[#0065FD] hover:bg-[#DCE8FF]"
      }`}
    >
      {label}
    </button>
  );

  const remindBlock = (week) =>
    canRemind ? (
      <div className="mt-2">
        {draft?.weekStart === week.weekStart && draft?.action === "remind" ? (
          <div className="rounded-[6px] border border-[#E5E8F3] bg-[#F8FAFC] px-2.5 py-2">
            <div className="text-[11px] font-InterMedium text-[#5F6477] mb-1">Remind {name} by</div>
            <div className="flex items-center gap-3">
              {[
                ["push", "App notification"],
                ["email", "Email"],
                ["sms", "Text (not available yet)"],
              ].map(([key, label]) => (
                <label key={key} className="inline-flex items-center gap-1 text-[12px] font-InterRegular text-[#1A2332]">
                  <input
                    type="checkbox"
                    checked={channels[key]}
                    disabled={key === "sms"}
                    onChange={(event) => setChannels({ ...channels, [key]: event.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="mt-1.5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="px-2.5 py-1 rounded-[6px] text-[12px] font-InterMedium text-[#5F6477] hover:bg-[#F0F1F5]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={(!channels.push && !channels.email) || busy != null}
                onClick={() =>
                  run(
                    week.weekStart,
                    "remind",
                    () =>
                      ReportService.remindTimecard(
                        technician.technicianId,
                        week.weekStart,
                        ["push", "email"].filter((key) => channels[key])
                      ),
                    (result) => {
                      const sent = ["push", "email"].filter((key) => result?.[key] === "sent");
                      const failed = ["push", "email"].filter((key) => result?.[key] === "failed");
                      return sent.length
                        ? `Reminded ${name} by ${sent.join(" and ")}${failed.length ? ` (${failed.join(", ")} failed)` : ""}`
                        : "The reminder could not be sent";
                    }
                  )
                }
                className="px-2.5 py-1 rounded-[6px] text-[12px] font-InterMedium text-white bg-[#0065FD] disabled:bg-[#CAD5E2]"
              >
                Send reminder
              </button>
            </div>
          </div>
        ) : (
          actionButton("Remind", () => setDraft({ weekStart: week.weekStart, action: "remind", reason: "" }))
        )}
      </div>
    ) : null;

  return (
    <span ref={anchorRef} className="inline-flex items-center">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((previous) => !previous);
        }}
        title={title}
        aria-expanded={open}
        className={`shrink-0 inline-flex items-center gap-1 px-2 py-[2px] rounded-[6px] border text-[11px] leading-4 font-InterMedium whitespace-nowrap ${style.chip}`}
      >
        <style.Icon />
        {chipText}
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              style={{ top: rect.top, left: rect.left, width: rect.width }}
              onClick={(event) => event.stopPropagation()}
              className="fixed z-[60] rounded-[8px] border border-[#C9CEE2] bg-white shadow-[0px_8px_24px_rgba(16,24,40,0.12)]"
            >
              <div className="px-3 pt-2.5 pb-2 border-b border-[#EEF0F9]">
                <p className="font-InterSemiBold text-[13px] leading-[18px] text-[#1A2332]">Time card sign-off · {name}</p>
                <p className="mt-0.5 font-InterRegular text-[12px] leading-4 text-[#868DA6]">
                  {signoff.cycle === 1 ? "Monday – Sunday" : "Sunday – Saturday"} weeks. The technician signs from the app; every step here is written to the hours history.
                </p>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                {weeks.map((week) => {
                  const sub = week.submission;
                  const weekStyle = STATUS_STYLE[week.status];
                  const submittedBy = actorFor("TIMECARD_SUBMITTED", week.weekStart);
                  const byOffice = sub?.submittedById != null && sub.submittedById !== technician.technicianId;
                  const decidedBy = actorFor(
                    week.status === "REOPENED" ? "TIMECARD_REOPENED" : "TIMECARD_REOPEN_DENIED",
                    week.weekStart
                  );
                  return (
                    <div key={week.weekStart} className="px-3 py-2.5 border-b border-[#F1F5F9] last:border-b-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-InterSemiBold text-[13px] leading-[18px] text-[#1A2332]">
                          {describeWeek(week.weekStart)}
                          {week.current ? <span className="ml-1.5 font-InterRegular text-[11px] text-[#868DA6]">this week</span> : null}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-[1px] rounded-[6px] border text-[11px] leading-4 font-InterMedium ${weekStyle.chip}`}>
                          <weekStyle.Icon />
                          {weekStyle.label}
                        </span>
                      </div>

                      {!week.covered ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#868DA6]">
                          {signoff.signoffEnabled
                            ? `Ended before weekly sign-off was turned on for ${signoff.signoffBranch ?? "the branch"} (${moment(signoff.signoffSince).format("MMM D, YYYY")}). Shown as a record; nothing to do.`
                            : `Weekly sign-off is off for ${signoff.signoffBranch ?? "the branch"}. Shown as a record; nothing to do.`}
                        </p>
                      ) : null}
                      {sub?.snapshot?.summary && week.status !== "REOPENED" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#45556C]">
                          Signed at RT {sub.snapshot.summary.regularTime ?? "—"} · OT {sub.snapshot.summary.overTime ?? "—"} · DT{" "}
                          {sub.snapshot.summary.doubleTime ?? "—"} · TT {sub.snapshot.summary.travelTime ?? "—"}
                          {sub.snapshot.summary.lunchTime && sub.snapshot.summary.lunchTime !== "00:00" ? ` after ${sub.snapshot.summary.lunchTime} lunch` : ""}.
                        </p>
                      ) : null}
                      {week.status === "SUBMITTED" || week.status === "REOPEN_REQUESTED" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#45556C]">
                          Submitted {when(sub.submittedAt)}
                          {byOffice ? ` by ${submittedBy ?? "the office"} on ${name}'s behalf` : submittedBy ? ` by ${submittedBy}` : ""}
                          {sub.submitReason ? ` — ${sub.submitReason}` : ""}
                        </p>
                      ) : null}
                      {sub?.changedAfterSubmitAt && week.status !== "REOPENED" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#92400E]">
                          Changed by the office after sign-off, {when(sub.changedAfterSubmitAt)}. The signature stands for the figures before that change.
                        </p>
                      ) : null}
                      {week.status === "REOPEN_REQUESTED" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#5B21B6]">
                          Asked to reopen {when(sub.requestedAt)}: “{sub.requestReason}”
                        </p>
                      ) : null}
                      {week.status === "SUBMITTED" && sub.decision === "DENIED" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#868DA6]">
                          Reopen declined {when(sub.decidedAt)}{decidedBy ? ` by ${decidedBy}` : ""}{sub.decisionReason ? `: ${sub.decisionReason}` : ""}
                        </p>
                      ) : null}
                      {week.status === "REOPENED" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#92400E]">
                          Reopened {when(sub.decidedAt)}{decidedBy ? ` by ${decidedBy}` : ""}
                          {sub.decision === "APPROVED" ? ` at ${name}'s request` : ""}
                          {sub.decisionReason ? ` — ${sub.decisionReason}` : ""}. Open to {name} until they submit it again.
                        </p>
                      ) : null}
                      {week.status === "OPEN" ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#45556C]">
                          {week.current
                            ? `This week is still in progress. ${name} can submit it from the app once their week is done.`
                            : week.windowOpen
                              ? `${name} can still submit this from the app until ${moment(signoff.lastWeekEnd).add(1, "day").format("ddd MMM D")}.`
                              : `${name}'s week to submit this has passed. Only the office can sign it now.`}
                        </p>
                      ) : null}
                      {week.current && (week.status === "SUBMITTED" || week.status === "REOPEN_REQUESTED") ? (
                        <p className="mt-1 font-InterRegular text-[12px] leading-4 text-[#868DA6]">
                          Signed before the week ended: nothing can be added for the rest of it unless it is reopened.
                        </p>
                      ) : null}

                      {/* What the office can do. */}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {week.covered && week.status === "REOPEN_REQUESTED" && canDecide ? (
                          <>
                            {actionButton(
                              "Approve",
                              () =>
                                run(
                                  week.weekStart,
                                  "approve",
                                  () => ReportService.decideTimecardReopen(technician.technicianId, week.weekStart, true),
                                  `Reopened ${describeWeek(week.weekStart)} for ${name}`
                                ),
                              "green"
                            )}
                            {actionButton("Deny", () => setDraft({ weekStart: week.weekStart, action: "deny", reason: "" }), "red")}
                          </>
                        ) : null}
                        {week.covered && week.status === "SUBMITTED" && canDecide
                          ? actionButton("Reopen", () => setDraft({ weekStart: week.weekStart, action: "reopen", reason: "" }))
                          : null}
                        {week.covered && week.status === "OPEN" && !week.windowOpen && canSubmitFor
                          ? actionButton(`Submit for ${name.split(",")[0].trim()}`, () =>
                              setDraft({ weekStart: week.weekStart, action: "submitFor", reason: "" })
                            )
                          : null}
                      </div>
                      {reasonBox(week, "deny", "Why not? Sent to the technician.", "Deny request", (reason) =>
                        run(
                          week.weekStart,
                          "deny",
                          () => ReportService.decideTimecardReopen(technician.technicianId, week.weekStart, false, reason || null),
                          `Declined to reopen ${describeWeek(week.weekStart)}`
                        ),
                        false
                      )}
                      {reasonBox(week, "reopen", "Why is the week being reopened? Sent to the technician.", "Reopen week", (reason) =>
                        run(
                          week.weekStart,
                          "reopen",
                          () => ReportService.reopenTimecard(technician.technicianId, week.weekStart, reason),
                          `Reopened ${describeWeek(week.weekStart)} for ${name}`
                        )
                      )}
                      {reasonBox(week, "submitFor", "Why is the office signing this week? Kept with the card.", "Submit week", (reason) =>
                        run(
                          week.weekStart,
                          "submitFor",
                          () => ReportService.submitTimecardFor(technician.technicianId, week.weekStart, reason),
                          `Submitted ${describeWeek(week.weekStart)} on ${name}'s behalf`
                        )
                      )}
                      {week.covered && (week.status === "OPEN" || week.status === "REOPENED") ? remindBlock(week) : null}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

export default SignOffChip;
